/**
 * Shopify Variant Title Updater
 * ─────────────────────────────────────────────────────────────────────────────
 * Listens for variant changes on a product page and updates the displayed
 * product title to reflect the selected variant's title.
 *
 * Only runs on the two product IDs listed in ENABLED_PRODUCT_IDS below.
 * All other product pages are ignored immediately at startup.
 *
 * USAGE
 * ─────
 * 1. Add a `data-product-title` attribute to the element that shows the title:
 *      <h1 class="product__title" data-product-title>{{ product.title }}</h1>
 *
 * 2. Expose the current product ID in your Liquid template so the script can
 *    read it.  The recommended approach is a data attribute on the <body>:
 *      <body data-product-id="{{ product.id }}">
 *
 *    If that isn't possible, the script also falls back to
 *    window.ShopifyAnalytics and the product JSON endpoint.
 *
 * 3. Include this script at the bottom of your product template, or in your
 *    theme's main JS bundle after the DOM is ready.
 *
 * COMPATIBILITY
 * ─────────────
 * Works with both classic Shopify themes (that dispatch a custom
 * `variant:changed` event) and modern themes using the URL-based approach
 * (`history.pushState` + `popstate`).  Falls back to watching the hidden
 * `#ProductSelect` <select> element for themes that don't dispatch events.
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  "use strict";
  console.log('variant-title-updater script started.')

  // ── Config ────────────────────────────────────────────────────────────────

  /**
   * The script will only execute on pages whose product ID is in this set.
   * IDs are stored as strings to avoid JS large-integer precision issues.
   */
  const ENABLED_PRODUCT_IDS = new Set([
    "10506048831761"
  ]);

  /**
   * CSS selector for the element whose text content should be updated.
   * Must match the element carrying `data-product-title` in your template.
   */
  const TITLE_SELECTOR = "[data-product-title]";

  /**
 * Map variant IDs to the exact title string you want displayed.
 * Add or remove entries as needed — IDs are strings to avoid precision issues.
 * Any variant ID not listed here will fall back to the base product title.
 */
const VARIANT_TITLE_MAP = {
  "53505747714321": "Amazing Cookies - Case of 48 Cookies"
};

  // ── Product ID guard ──────────────────────────────────────────────────────

  /**
   * Resolves the current product's ID as a string, trying three sources
   * in order of reliability:
   *   1. <body data-product-id="...">   (recommended — set it in Liquid)
   *   2. window.ShopifyAnalytics.meta.product.id
   *   3. /products/<handle>.js endpoint (async fallback)
   *
   * Returns null if the ID cannot be determined synchronously.
   */
  function getCurrentProductId() {
    console.log('getCurrentProductId function started.')
    // Source 1: body data attribute (fastest, no network call needed)
    const bodyId = document.body.getAttribute("data-product-id");
    console.log("[variant-title-updater] Raw body ID:", JSON.stringify(bodyId));
    if (bodyId) return String(bodyId).trim();

    // Source 2: ShopifyAnalytics global
    const analyticsId =
      window.ShopifyAnalytics &&
      window.ShopifyAnalytics.meta &&
      window.ShopifyAnalytics.meta.product &&
      window.ShopifyAnalytics.meta.product.id;
    if (analyticsId) return String(analyticsId).trim();

    return null;
  }

  /**
   * Checks whether the current page is one of the two enabled products.
   * Calls `onAllowed` immediately if the ID is already available, or after
   * an async product JSON fetch if we have to fall back to the endpoint.
   *
   * @param {Function} onAllowed  Called with no arguments when the product is allowed.
   */
  function ifProductAllowed(onAllowed) {
    console.log('ifProductAllowed function started.')
    const id = getCurrentProductId();
    console.log("[variant-title-updater] Checking ID:", id, "| Allowed:", ENABLED_PRODUCT_IDS.has(id));

    if (id !== null) {
      // We have the ID synchronously — check and bail early if not in the set.
      if (ENABLED_PRODUCT_IDS.has(id)) {
        onAllowed();
      }
      return;
    }

    // Async fallback: fetch product JSON and read the ID from there.
    const handle = getProductHandle();
    if (!handle) return;

    fetch(`/products/${handle}.js`)
      .then((res) => res.json())
      .then((product) => {
        if (ENABLED_PRODUCT_IDS.has(String(product.id))) {
          onAllowed();
        }
      })
      .catch((err) => {
        console.warn("[variant-title-updater] Could not verify product ID:", err);
      });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  /**
   * The base product title (no variant suffix).
   * Shopify exposes this via the global `window.ShopifyAnalytics` object,
   * or we fall back to reading the current DOM text on init.
   */
  const getBaseTitle = () => {
    const titleWrapper = document.querySelector('[data-base-title]');
    if (titleWrapper) return titleWrapper.getAttribute('data-base-title');

    // Fallbacks if the wrapper isn't found.
    return (
      (window.ShopifyAnalytics &&
        window.ShopifyAnalytics.meta &&
        window.ShopifyAnalytics.meta.product &&
        window.ShopifyAnalytics.meta.product.title) ||
      (document.querySelector(TITLE_SELECTOR) || {}).textContent?.trim() ||
      ""
    );
  };

  /**
   * Returns the element responsible for displaying the product title,
   * or null if it cannot be found in the DOM.
   *
   * @returns {Element|null}
   */
  function getTitleElement() {
    const el = document.querySelector(TITLE_SELECTOR);
    console.log("[variant-title-updater] Title element found:", el);
    return el;
  }

  /**
   * Updates the product title element.
   * - If the variant has a meaningful title (not just "Default Title"),
   *   appends it to the base title: "Base Product — Variant Name".
   * - Otherwise restores the plain base title.
   *
   * @param {Object} variant  A Shopify variant object (from JSON or the event).
   */
    function updateTitle(variant) {
      console.log("[variant-title-updater] updateTitle called with variant:", variant);
      console.log("[variant-title-updater] variant.id:", variant && String(variant.id));
      console.log("[variant-title-updater] VARIANT_TITLE_MAP lookup:", VARIANT_TITLE_MAP[String(variant?.id)]);

      const elements = document.querySelectorAll(TITLE_SELECTOR);
      console.log("[variant-title-updater] Elements found:", elements.length, elements);
      if (!elements.length) {
        console.log("[variant-title-updater] No title elements found for selector:", TITLE_SELECTOR);
        return;
      }

      const baseTitle = getBaseTitle();
      const newTitle = variant
        ? (VARIANT_TITLE_MAP[String(variant.id)] || baseTitle)
        : baseTitle;

      elements.forEach((el) => (el.textContent = newTitle));
    }

  /**
   * Derives the product handle from the current URL path.
   * Works for standard Shopify URLs: /products/<handle>
   *
   * @returns {string|null}
   */
  function getProductHandle() {
    console.log('getProductHandle function started.')
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  // ── Core logic (only runs if product ID is in the allowed set) ────────────

  function init() {
    console.log('init function started.');
    console.log("[variant-title-updater] init() called — listeners being attached");

    // ── Strategy 1: Dawn's native variant change event ──────────────────────────
      // Listen directly on the individual radio/select inputs inside Dawn's
      // variant picker, which carry the new variant ID as their value at the
      // moment the change event fires.

      document.querySelectorAll('variant-radios input, variant-selects select').forEach((input) => {
        input.addEventListener('change', function () {
          // Find the currently checked/selected variant ID across all option inputs.
          const variantPicker = this.closest('variant-radios, variant-selects');
          if (!variantPicker) return;

          // Build the selected options array from all inputs in the picker.
          const selectedOptions = Array.from(
            variantPicker.querySelectorAll('input[type="radio"]:checked, select')
          ).map((el) => el.value);

          console.log("[variant-title-updater] Selected options:", selectedOptions);

          const productHandle = getProductHandle();
          if (!productHandle) return;

          fetch(`/products/${productHandle}.js`)
            .then((res) => res.json())
            .then((product) => {
              // Match the selected options against the full variant list.
              const variant = product.variants.find((v) =>
                v.options.every((opt, i) => opt === selectedOptions[i])
              );
              console.log("[variant-title-updater] Matched variant:", variant);
              updateTitle(variant || null);
            })
            .catch((err) => {
              console.warn("[variant-title-updater] Could not load product JSON:", err);
            });
        });
      });

    // ── Strategy 2: Native `change` on the hidden variant <select> ─────────
    // Classic themes use a hidden <select id="ProductSelect"> whose value is
    // the variant ID.  We map that ID back to a variant object via the
    // product JSON endpoint.

    const productSelect = document.getElementById("ProductSelect");

    if (productSelect) {
      const productHandle = getProductHandle();

      if (productHandle) {
        fetch(`/products/${productHandle}.js`)
          .then((res) => res.json())
          .then((product) => {
            const variantMap = {};
            product.variants.forEach((v) => (variantMap[v.id] = v));

            productSelect.addEventListener("change", function () {
              const selectedVariant = variantMap[parseInt(this.value, 10)];
              updateTitle(selectedVariant || null);
            });
          })
          .catch((err) => {
            console.warn("[variant-title-updater] Could not load product JSON:", err);
          });
      }
    }

    // ── Strategy 3: URL / history-based navigation (headless / modern themes)
    // Some themes push a new URL (e.g. ?variant=12345) on selection.
    // We watch for those changes and re-fetch the current variant from the URL.

    const syncTitleFromUrl = () => {
      const variantId = new URLSearchParams(window.location.search).get("variant");
      if (!variantId) return;

      const productHandle = getProductHandle();
      if (!productHandle) return;

      fetch(`/products/${productHandle}.js`)
        .then((res) => res.json())
        .then((product) => {
          const variant = product.variants.find(
            (v) => v.id === parseInt(variantId, 10)
          );
          updateTitle(variant || null);
        })
        .catch((err) => {
          console.warn("[variant-title-updater] URL sync failed:", err);
        });
    }

    window.addEventListener("popstate", syncTitleFromUrl);

    const _pushState = history.pushState.bind(history);
    history.pushState = function (...args) {
        _pushState(...args);
        syncTitleFromUrl();
    };

    // Sync on page load in case a variant is pre-selected via the URL.
    syncTitleFromUrl();
  }

  // ── Entry point ───────────────────────────────────────────────────────────
  // Check the product ID first; only call init() if it's in the allowed set.

  ifProductAllowed(init);

})();